import { useState, useEffect, useRef, useMemo } from "react";
import moment from "moment";
import "moment-timezone";
import axios from "../config/axios";
import { useConfig } from "./useConfig";
import useGlobalStore from "../store/global.store";
import useBeaconControl from "./useBeaconControl";

const TZ = "America/New_York";

const DOOR2_VIEWS = new Set([
  "VIP__roof",
  "VIP__hotel",
  "VIP__castle",
  "VIP__boat",
  "VIP_door_2_selector",
  "VIP__multiview",
]);

// ---------- Shared state (single source of truth across all instances) ----------
const store = {
  enabled: localStorage.getItem("fairyFlight") === "on",
  vibOn: localStorage.getItem("fairyFlightVib") !== "off",
};
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn());

const setStoreEnabled = (next) => {
  if (store.enabled === next) return;
  store.enabled = next;
  localStorage.setItem("fairyFlight", next ? "on" : "off");
  emit();
};

const setStoreVibOn = (next) => {
  if (store.vibOn === next) return;
  store.vibOn = next;
  localStorage.setItem("fairyFlightVib", next ? "on" : "off");
  emit();
};

/**
 * Lightweight hook: reads shared state & exposes toggles.
 * Use this in UI components (e.g. MagicBand) that only need to read/toggle.
 */
export function useFairyFlightControls() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const listener = () => setTick((n) => n + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  return {
    enabled: store.enabled,
    vibOn: store.vibOn,
    toggleEnabled: () => setStoreEnabled(!store.enabled),
    toggleVib: () => setStoreVibOn(!store.vibOn),
  };
}

// ---------- CSV helpers ----------
function parseCsvTime(t) {
  const [m, s] = String(t || "")
    .trim()
    .split(":")
    .map(Number);
  if (!Number.isFinite(m) || !Number.isFinite(s)) return NaN;
  return (m * 60 + s) * 1000;
}

function parseCsv(text) {
  const lines = text.split(/\r?\n/);
  return lines
    .slice(1)
    .map((line) => {
      const cols = line.split(",");
      const t = cols[0]?.trim();
      const fx = cols[1]?.trim();
      const vib = cols[2]?.trim() || "OFF";
      if (!t || !fx) return null;
      const offsetMs = parseCsvTime(t);
      if (isNaN(offsetMs)) return null;
      return { offsetMs, fx, vib };
    })
    .filter(Boolean)
    .sort((a, b) => a.offsetMs - b.offsetMs);
}

function fireFairyCmd(deviceId, fx) {
  if (fx === "CANCEL") {
    return axios
      .post("/esp/cancel", { deviceId }, { withCredentials: true })
      .catch((err) => console.error("[useFairyFlight] Cancel failed:", err));
  }
  return axios
    .post(
      "/esp/beacon-show",
      { deviceId, fx, vib: "OFF" },
      { withCredentials: true },
    )
    .catch((err) =>
      console.error("[useFairyFlight] Command failed:", { fx }, err),
    );
}

/**
 * Full hook: shared state + scheduler. Mount this ONCE at the app level
 * (FairyFlightRunner in App.js). Do NOT call from individual screens —
 * they should use useFairyFlightControls() instead.
 */
export function useFairyFlight() {
  const { config } = useConfig();
  const device = useGlobalStore((state) => state.device);
  const hasBeaconAddOn = useGlobalStore((state) => state.hasBeaconAddOn);
  const { isController } = useBeaconControl();

  // Pull shared state
  const controls = useFairyFlightControls();
  const { enabled, vibOn } = controls;

  const commandsRef = useRef([]);
  const firedRef = useRef(new Set());
  const initializedRef = useRef(false);
  const showStartEpochMsRef = useRef(null);

  const beaconDeviceId = device?.magic_beacon_device?._id ?? null;
  const screenView = device?.screen_view ?? "";
  const isOnDoor2 = DOOR2_VIEWS.has(screenView);

  const showInfo = useMemo(() => {
    if (!config) return null;

    const startTimeStr = config.vipShowOneStartTime || config.mkFWmanualVIP;
    if (!startTimeStr) return null;

    let showType;
    if (config.Beacon_Show_Override) {
      showType = config.Beacon_Show_Override;
    } else if (config.vipShowOneStartTime) {
      showType = "hea";
    } else {
      const showName = String(config.showName ?? "");
      showType =
        showName === "3" ? "xmas" : showName === "2" ? "halloween" : "hea";
    }

    if (showType === "halloween") return null;

    return { showType, startTimeStr };
  }, [config]);

  const csvKey = useMemo(() => {
    if (!showInfo) return null;
    const vibSuffix = vibOn ? "vib" : "no";
    return `fairy_${showInfo.showType}_${vibSuffix}`;
  }, [showInfo, vibOn]);

  useEffect(() => {
    if (!csvKey) {
      commandsRef.current = [];
      return;
    }

    fetch(`/firework/${csvKey}.csv`)
      .then((r) => {
        if (!r.ok) throw new Error(`CSV fetch failed: ${r.status}`);
        return r.text();
      })
      .then((text) => {
        commandsRef.current = parseCsv(text);
        firedRef.current = new Set();
        initializedRef.current = false;
        console.log(
          `[useFairyFlight] Loaded ${commandsRef.current.length} commands from ${csvKey}.csv`,
        );
      })
      .catch((err) => {
        console.error("[useFairyFlight] Failed to load CSV:", err);
      });
  }, [csvKey]);

  useEffect(() => {
    if (!showInfo?.startTimeStr) {
      showStartEpochMsRef.current = null;
      return;
    }
    const todayStr = moment().tz(TZ).format("YYYY-MM-DD");
    const epoch = moment
      .tz(`${todayStr} ${showInfo.startTimeStr}`, "YYYY-MM-DD HH:mm", TZ)
      .valueOf();
    showStartEpochMsRef.current = epoch;

    firedRef.current = new Set();
    initializedRef.current = false;
  }, [showInfo?.startTimeStr]);

  useEffect(() => {
    if (!enabled || !beaconDeviceId || isOnDoor2 || !hasBeaconAddOn || !isController) return;

    const tick = () => {
      const start = showStartEpochMsRef.current;
      const cmds = commandsRef.current;
      if (!start || !cmds.length) return;

      const elapsed = Date.now() - start;
      if (elapsed < 0) return;

      if (!initializedRef.current) {
        initializedRef.current = true;
        cmds.forEach((cmd, i) => {
          if (cmd.offsetMs <= elapsed) {
            firedRef.current.add(i);
          }
        });
        const skipped = firedRef.current.size;
        if (skipped > 0) {
          console.log(
            `[useFairyFlight] Late entry — skipped ${skipped} past commands`,
          );
        }
      }

      cmds.forEach((cmd, i) => {
        if (!firedRef.current.has(i) && cmd.offsetMs <= elapsed) {
          firedRef.current.add(i);
          console.log(
            `[useFairyFlight] Firing +${cmd.offsetMs}ms: fx=${cmd.fx}`,
          );
          fireFairyCmd(beaconDeviceId, cmd.fx);
        }
      });
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [enabled, beaconDeviceId, isOnDoor2, csvKey, hasBeaconAddOn, isController]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    initializedRef.current = false;
  }, [enabled, isOnDoor2]);

  return {
    ...controls,
    showType: showInfo?.showType ?? null,
    csvKey,
    isActive: enabled && !isOnDoor2 && !!beaconDeviceId && !!showInfo,
  };
}
